import React, { useEffect, useRef, useState } from 'react';
import { ChildProfile } from '../../models/childProfile';
import { StaffProfile } from '../../models/staffProfile';
import { formatDisplayDate } from '../../services/dateUtils';
import ChildSelectCard from './ChildSelectCard';

export type RuleFrequency = 'weekly' | 'biweekly' | 'monthly' | 'once';

export interface ScheduleSession {
  id: string;
  dayOfWeek: string;
  lane: number;
  startTime: string;
  endTime: string;
  childId: number | null;
  teacherIds: number[];
  locationIds: number[];
  // Recurrence rule metadata
  frequency: RuleFrequency;
  startDate: string;       // YYYY-MM-DD — first date the rule applies
  endDate: string | null;  // YYYY-MM-DD — null means ongoing
  // Set after the rule has been persisted server-side. Edits to a session
  // with a serverId require the user to choose a scope (one-off vs. persistent).
  serverId?: number;
}

/** A five-occurrence recurrence strip, drawn in `currentColor`.
 *  `skip` punches a gap out of the middle of the series; `cut` shows the
 *  series running solid, stopping at a bar, then fading out. */
const OccurrenceStrip: React.FC<{ variant: 'skip' | 'cut' }> = ({ variant }) => (
  <svg
    className="wsg-sdp__strip"
    viewBox="0 0 40 12"
    width="40"
    height="12"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="3" cy="6" r="2.4" fill="currentColor" />
    <circle cx="11.5" cy="6" r="2.4" fill="currentColor" />
    {variant === 'skip' ? (
      <>
        <circle cx="20" cy="6" r="2.1" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.45" />
        <circle cx="28.5" cy="6" r="2.4" fill="currentColor" />
        <circle cx="37" cy="6" r="2.4" fill="currentColor" />
      </>
    ) : (
      <>
        <rect x="15" y="0.9" width="1.5" height="10.2" rx="0.75" fill="currentColor" />
        <circle cx="21.5" cy="6" r="2.1" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <circle cx="29.5" cy="6" r="2.1" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.26" />
        <circle cx="37.5" cy="6" r="2.1" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.15" />
      </>
    )}
  </svg>
);

const FREQUENCY_LABELS: Record<RuleFrequency, string> = {
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  once: 'One-off',
};

interface SessionDetailPanelProps {
  /** The selected session. The panel only ever edits a session — new-session
   *  defaults are edited via SessionDefaultsModal, not here. */
  session: ScheduleSession;
  childProfiles: ChildProfile[];
  staffProfiles: StaffProfile[];
  onUpdate: (updates: Partial<ScheduleSession>) => void;
  onDelete: () => void;
  onClose: () => void;
  /** Fresh sessions (still in their initial-creation window) auto-save each
   *  edit and don't surface a Save button. Settled sessions require an
   *  explicit Save click which then opens the scope-choice modal. */
  isFresh?: boolean;
  /** True when the panel has accumulated unsaved draft edits for a settled session. */
  isDirty?: boolean;
  /** Disabled when child is null, or in any other state where Save isn't valid. */
  canSave?: boolean;
  onSave?: () => void;
  /** True when the session is cancelled for the currently-viewed date. */
  cancelledThisDate?: boolean;
  /** Free-text reason captured at cancel time (e.g. 'Bank holiday'). */
  cancelledReason?: string | null;
  /** The calendar date (YYYY-MM-DD) the panel is currently scoped to (for banners). */
  editDate?: string | null;
  /** Restore a previously-cancelled session for `editDate`. */
  onRestore?: () => void;
  /** True when `editDate` carries a 'modified' override — the times and
   *  teachers shown are this date's, not the rule's. */
  overriddenThisDate?: boolean;
  /** The rule's own times, shown in the override banner so the user can see
   *  what "the usual time" actually is. */
  ruleTimes?: { startTime: string; endTime: string } | null;
  /** Drop this date's override and go back to following the rule. */
  onRemoveOverride?: () => void;
  /** Label for the Save button. An override-only edit saves straight to this
   *  date, so it says so rather than implying the usual scope question. */
  saveLabel?: string;
  /** Settled-only: cancel just `editDate` (writes a 'cancelled' exception). */
  onCancelForDate?: () => void;
  /** Settled-only: end-date the rule from `editDate` forwards. Should confirm before firing. */
  onDeleteFromDate?: () => void;
  /** Hard-block message: this session double-books the child. Save is disabled while set. */
  conflictError?: string | null;
  /** Soft warning: a teacher is assigned to another overlapping session. */
  conflictWarning?: string | null;
}

const SessionDetailPanel: React.FC<SessionDetailPanelProps> = ({
  session,
  childProfiles,
  staffProfiles,
  onUpdate,
  onDelete,
  onClose,
  isFresh = true,
  isDirty = false,
  canSave = false,
  onSave,
  cancelledThisDate = false,
  cancelledReason = null,
  editDate = null,
  onRestore,
  overriddenThisDate = false,
  ruleTimes = null,
  onRemoveOverride,
  saveLabel = 'Save changes',
  onCancelForDate,
  onDeleteFromDate,
  conflictError = null,
  conflictWarning = null,
}) => {
  const activeChildId = session.childId;
  const activeChild = activeChildId != null
    ? childProfiles.find(c => c.id === activeChildId) ?? null
    : null;

  const childColor = activeChild?.colour_code ?? '#9aa89a';

  const activeTeacherIds = session.teacherIds;
  const selectedTeachers = staffProfiles.filter(s => activeTeacherIds.includes(s.id));

  const [teachersOpen, setTeachersOpen] = useState(false);
  const [teacherQuery, setTeacherQuery] = useState('');
  const teachersRef = useRef<HTMLDivElement>(null);

  // Clear the quick-find whenever the teachers dropdown closes.
  useEffect(() => {
    if (!teachersOpen) setTeacherQuery('');
  }, [teachersOpen]);

  // Close on outside click or Escape (the child dropdown handles its own —
  // see ChildSelectCard)
  useEffect(() => {
    if (!teachersOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (teachersRef.current && !teachersRef.current.contains(e.target as Node)) {
        setTeachersOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTeachersOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [teachersOpen]);

  const toggleTeacher = (id: number) => {
    const isSelected = activeTeacherIds.includes(id);
    const next = isSelected
      ? activeTeacherIds.filter(tid => tid !== id)
      : [...activeTeacherIds, id];
    onUpdate({ teacherIds: next });
  };

  return (
    <div
      className="wsg-sdp"
      style={{ ['--child-color' as string]: childColor } as React.CSSProperties}
    >
      <div className="wsg-sdp__header">
        <h6 className="wsg-sdp__title">Session</h6>
        <button
          className="wsg-sdp__close"
          onClick={onClose}
          title="Close"
        >
          <i className="fas fa-times" />
        </button>
      </div>

      {cancelledThisDate && (
        <div
          style={{
            background: '#fff7e6',
            border: '1px solid #f0c46c',
            borderRadius: 6,
            padding: '0.6rem 0.75rem',
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
          }}
        >
          <div style={{ marginBottom: 6 }}>
            <i className="fas fa-ban me-2" style={{ color: '#a86a00' }} />
            Cancelled for <strong>{editDate ? formatDisplayDate(editDate) : 'this date'}</strong>.
          </div>
          {cancelledReason && (
            <div
              style={{
                marginBottom: 8,
                fontSize: '0.82rem',
                fontStyle: 'italic',
                color: '#7a5400',
              }}
            >
              Reason: {cancelledReason}
            </div>
          )}
          {onRestore && (
            <button
              type="button"
              className="btn btn-sm btn-outline-warning w-100"
              data-help="schedule.restore-date"
              onClick={onRestore}
            >
              Restore session for this date
            </button>
          )}
        </div>
      )}

      {/* Override banner — this date's values differ from the rule's. Same
          geometry as the cancelled banner above; blue because it's telling the
          user where they are, not stopping them. */}
      {overriddenThisDate && !cancelledThisDate && (
        <div
          style={{
            background: '#eef4fd',
            border: '1px solid #a9c5ea',
            borderRadius: 6,
            padding: '0.6rem 0.75rem',
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
          }}
        >
          <div style={{ marginBottom: 6 }}>
            <i className="fas fa-pen me-2" style={{ color: '#2c5b96' }} />
            Changed for <strong>{editDate ? formatDisplayDate(editDate) : 'this date'}</strong> only.
          </div>
          <div style={{ marginBottom: 8, fontSize: '0.82rem', color: '#3f6795' }}>
            {ruleTimes
              ? <>Usually {ruleTimes.startTime}&ndash;{ruleTimes.endTime}. Edits here apply to this date alone.</>
              : <>Edits here apply to this date alone.</>}
          </div>
          {onRemoveOverride && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary w-100"
              onClick={onRemoveOverride}
            >
              Revert to the usual time
            </button>
          )}
        </div>
      )}

      {/* Conflict banners — hard block (child double-book) then soft warning
          (teacher double-book). The hard block also disables Save upstream. */}
      {conflictError && (
        <div
          role="alert"
          style={{
            background: '#fdecec',
            border: '1px solid #e3a0a0',
            borderRadius: 6,
            padding: '0.6rem 0.75rem',
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
            color: '#8a2020',
          }}
        >
          <i className="fas fa-triangle-exclamation me-2" />
          {conflictError}
        </div>
      )}
      {!conflictError && conflictWarning && (
        <div
          style={{
            background: '#fff7e6',
            border: '1px solid #f0c46c',
            borderRadius: 6,
            padding: '0.6rem 0.75rem',
            marginBottom: '0.75rem',
            fontSize: '0.85rem',
            color: '#7a5400',
          }}
        >
          <i className="fas fa-circle-info me-2" />
          {conflictWarning}
        </div>
      )}

      {/* Child — hero chip with custom dropdown (shared with the defaults modal) */}
      <ChildSelectCard
        childProfiles={childProfiles}
        activeChildId={activeChildId}
        onSelect={(id) => onUpdate({ childId: id })}
        readOnly={overriddenThisDate}
      />

      {/* Teachers — multi-select from editor staff */}
      <div className="wsg-sdp__teachers-wrap" ref={teachersRef}>
        <span className="wsg-sdp__label">Teachers</span>
        <button
          type="button"
          className={`wsg-sdp__teachers-trigger${teachersOpen ? ' is-open' : ''}`}
          onClick={() => setTeachersOpen(v => !v)}
          aria-haspopup="listbox"
          aria-expanded={teachersOpen}
        >
          {selectedTeachers.length === 0 ? (
            <span className="wsg-sdp__teachers-empty">No teachers assigned</span>
          ) : (
            <div className="wsg-sdp__teachers-stack">
              {selectedTeachers.slice(0, 4).map((t, i) => (
                <span
                  key={t.id}
                  className="wsg-sdp__teachers-chip"
                  style={{ background: t.colourCode, zIndex: selectedTeachers.length - i }}
                  title={t.name}
                >
                  {t.initials}
                </span>
              ))}
              {selectedTeachers.length > 4 && (
                <span className="wsg-sdp__teachers-more">+{selectedTeachers.length - 4}</span>
              )}
            </div>
          )}
          <i className={`fas fa-chevron-down wsg-sdp__chevron${teachersOpen ? ' is-open' : ''}`} />
        </button>

        {teachersOpen && (() => {
          const tq = teacherQuery.trim().toLowerCase();
          const filteredStaff = tq
            ? staffProfiles.filter(s => s.name.toLowerCase().includes(tq))
            : staffProfiles;
          return (
          <div className="wsg-sdp__dropdown" role="listbox" aria-multiselectable="true">
            <div className="wsg-sdp__search">
              <i className="fas fa-search" aria-hidden />
              <input
                type="text"
                placeholder="Search teachers…"
                value={teacherQuery}
                onChange={(e) => setTeacherQuery(e.target.value)}
                autoFocus
                aria-label="Search teachers"
              />
            </div>
            {staffProfiles.length === 0 ? (
              <div className="wsg-sdp__empty">No editors available</div>
            ) : filteredStaff.length === 0 ? (
              <div className="wsg-sdp__empty">No matches</div>
            ) : (
              filteredStaff.map(s => {
                const selected = activeTeacherIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`wsg-sdp__option${selected ? ' is-selected' : ''}`}
                    onClick={() => toggleTeacher(s.id)}
                  >
                    <span
                      className="wsg-sdp__option-circle"
                      style={{ background: s.colourCode }}
                    >
                      {s.initials}
                    </span>
                    <span className="wsg-sdp__option-name">{s.name}</span>
                    {selected && (
                      <i className="fas fa-check wsg-sdp__option-check" />
                    )}
                  </button>
                );
              })
            )}
          </div>
          );
        })()}
      </div>

      {/* Location — hidden until the app supports locations on sessions.
          Flip this guard when it does. */}
      {false && (
        <div className="wsg-sdp__field">
          <span className="wsg-sdp__label">Location</span>
          <select disabled>
            <option>No location assigned</option>
          </select>
        </div>
      )}

      <hr className="wsg-sdp__hr" />

      {/* When — day + time range + recurrence rule */}
      <div className="wsg-sdp__when">
          <span className="wsg-sdp__day">{session.dayOfWeek}</span>
          <div className="wsg-sdp__time-row">
            <input
              type="time"
              className="wsg-sdp__time"
              value={session.startTime}
              onChange={(e) => {
                const v = e.target.value;
                if (v && v < session.endTime) onUpdate({ startTime: v });
              }}
              aria-label="Start time"
            />
            <span className="wsg-sdp__arrow" aria-hidden="true">&rarr;</span>
            <input
              type="time"
              className="wsg-sdp__time"
              value={session.endTime}
              onChange={(e) => {
                const v = e.target.value;
                if (v && v > session.startTime) onUpdate({ endTime: v });
              }}
              aria-label="End time"
            />
          </div>

          <div className="wsg-sdp__when-divider" />

          {/* Below the divider is the recurring rule, which a per-date override
              has no say over — schedule_exception carries times, teachers and
              rooms, and nothing else. Disabled rather than hidden so it's clear
              why, and where the way back is. */}
          {overriddenThisDate && (
            <div
              style={{
                fontSize: '0.78rem',
                color: '#6b7280',
                marginBottom: '0.5rem',
                lineHeight: 1.35,
              }}
            >
              <i className="fas fa-lock me-2" aria-hidden />
              These belong to the repeating rule, not this date. Revert this date
              above to change them.
            </div>
          )}

          <div className="wsg-sdp__when-row">
            <span className="wsg-sdp__when-label">Repeats</span>
            <select
              className="wsg-sdp__when-select"
              value={session.frequency}
              disabled={overriddenThisDate}
              onChange={(e) => onUpdate({ frequency: e.target.value as RuleFrequency })}
            >
              {/* Monthly is no longer offered: "same date each month" lands on a
                  different weekday most months, which a weekday grid can't draw.
                  Kept only so a legacy monthly rule still shows its own value. */}
              {(Object.keys(FREQUENCY_LABELS) as RuleFrequency[])
                .filter(f => f !== 'monthly' || session.frequency === 'monthly')
                .map(f => (
                <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>
              ))}
            </select>
          </div>

          <div className="wsg-sdp__when-row">
            <span className="wsg-sdp__when-label">Start date</span>
            <input
              type="date"
              className="wsg-sdp__when-input"
              value={session.startDate}
              disabled={overriddenThisDate}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                // Keep start <= end
                if (session.endDate && v > session.endDate) {
                  onUpdate({ startDate: v, endDate: null });
                } else {
                  onUpdate({ startDate: v });
                }
              }}
            />
          </div>

          <div className="wsg-sdp__when-row">
            <span className="wsg-sdp__when-label">End date</span>
            {session.endDate == null ? (
              <button
                type="button"
                className="wsg-sdp__when-addend"
                disabled={overriddenThisDate}
                onClick={() => {
                  // Default: 3 months after startDate
                  const d = new Date(session.startDate + 'T00:00');
                  d.setMonth(d.getMonth() + 3);
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  onUpdate({ endDate: `${yyyy}-${mm}-${dd}` });
                }}
              >
                <i className="fas fa-infinity" />
                <span>Ongoing &mdash; set end date</span>
              </button>
            ) : (
              <>
                <input
                  type="date"
                  className="wsg-sdp__when-input"
                  value={session.endDate}
                  min={session.startDate}
                  disabled={overriddenThisDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && v >= session.startDate) onUpdate({ endDate: v });
                  }}
                />
                <button
                  type="button"
                  className="wsg-sdp__when-clear"
                  disabled={overriddenThisDate}
                  onClick={() => onUpdate({ endDate: null })}
                  title="Clear end date (ongoing)"
                >
                  <i className="fas fa-times" />
                </button>
              </>
            )}
          </div>
      </div>

      {!isFresh && onSave && (
        <button
          type="button"
          className="btn btn-primary w-100 mt-3"
          disabled={!isDirty || !canSave}
          onClick={onSave}
        >
          {saveLabel}
        </button>
      )}

      {/* Fresh session: single Delete button (no scope choice — it's just being created). */}
      {isFresh && (
        <button
          className="wsg-sdp__delete wsg-sdp__delete--pinned"
          onClick={onDelete}
        >
          <i className="fas fa-trash" />
          <span>Delete session</span>
        </button>
      )}

      {/* Settled session: the two date-scoped actions, pinned to the foot of
          the panel. Everything above this rule edits the recurring rule;
          everything below it acts on the single date the grid is showing.
          Hidden when the session is already cancelled for this date — the
          Restore banner handles that case. */}
      {!isFresh && !cancelledThisDate && (onCancelForDate || onDeleteFromDate) && (
        <div className="wsg-sdp__datebar" data-help="schedule.date-bar">
          <span className="wsg-sdp__datebar-eyebrow">
            {editDate ? `This date · ${formatDisplayDate(editDate)}` : 'This date'}
          </span>

          {onCancelForDate && (
            <button
              type="button"
              className="wsg-sdp__dateact wsg-sdp__dateact--skip"
              data-help="schedule.cancel-date"
              onClick={onCancelForDate}
            >
              <OccurrenceStrip variant="skip" />
              <span className="wsg-sdp__dateact-text">
                <span className="wsg-sdp__dateact-title">Cancel for this date</span>
                <span className="wsg-sdp__dateact-note">You can restore it any time.</span>
              </span>
            </button>
          )}

          {/* Cancelling is a per-date act, so it stays — it converts this
              date's override into a cancellation. Ending the rule is not:
              it rewrites the rule and would strand the override behind it. */}
          {onDeleteFromDate && !overriddenThisDate && (
            <button
              type="button"
              className="wsg-sdp__dateact wsg-sdp__dateact--cut"
              data-help="schedule.end-rule"
              onClick={onDeleteFromDate}
            >
              <OccurrenceStrip variant="cut" />
              <span className="wsg-sdp__dateact-text">
                <span className="wsg-sdp__dateact-title">End the rule from this date</span>
                <span className="wsg-sdp__dateact-note">Past weeks stay untouched.</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionDetailPanel;
